// Style presets: iReal-Pro-style whole-band feels. A preset is a *template* —
// applying one seeds the individual mixer channels (bass, comp, drums) in one
// go, after which each channel is still independently editable (mix and match).
// The free-text chart `style` string (e.g. "Jazz-Bossa Nova") maps to a preset
// by keyword on load, so a tune arrives sounding roughly right.

import type { BassStyle, BassFeel } from './walkingBass';
import type { CompStyle } from './comping';
import type { CompFeel } from './compRhythm';
import type { PianoCompFamily } from './pianoComping';
import { DEFAULT_SWING_FEEL, type SwingFeel } from './swingFeel';
import {
  STRAIGHT_BAND_FEEL,
  SWING_BAND_FEEL,
  type BassAccent,
  type MelodyPhrasing,
  type Placement,
} from './bandFeel';

/** Which instrument realises the comp channel (#236). Guitar grips or piano
 * voicings — the same chart, a different pair of hands. */
/** What the comp channel plays: guitar grips, a two-handed piano, or the pianist's LEFT HAND
 * alone (the trio texture — the right hand takes the melody). */
export type CompInstrument = 'guitar' | 'piano' | 'piano-left';

export type StyleId =
  | 'swing'
  | 'up-swing'
  | 'ballad'
  | 'bossa'
  | 'samba'
  | 'choro'
  | 'shuffle'
  | 'funk'
  | 'waltz'
  | 'even-8ths'
  | 'boogaloo'
  | 'calypso'
  | 'cha-cha';

/** The full mixer band — everything a style preset seeds and the per-tune
 * memory remembers. A preset is a clean baseline: guitar on, bass + drums on,
 * no embellishments. */
export interface StyleBand {
  bassline: boolean;
  bassSolo: boolean; // guitar/comp muted?
  bassStyle: BassStyle;
  bassFeel: BassFeel;
  /** The rhythm-section convention, automated (user request Aug 2026): under
   * the head/solos/head policy the bass plays the heads in 2 and walks the
   * solo choruses in 4, overriding bassFeel per pass. Corpus-audited before
   * building: nobody WRITES this — 0 feel markings in the openbook 151, and
   * of 102 Jazz-1460 charts with staff text exactly one mentions a time-feel
   * device (Brilliant Corners' composed double-time repeat). Convention, not
   * notation — so it lives here, beside the policy, never in the chart. */
  bassFollowForm: boolean;
  /** Whether the band swings (the name is historical: it swings every part). */
  bassSwing: boolean;
  /** HOW it swings while `bassSwing` is on: Guitar Pro's triplet feel, the
   * level that swings and its ratio (swingFeel.ts). A detail of the groove, so
   * Medium Swing on a dotted feel reads "Medium Swing · modified". */
  swingFeel: SwingFeel;
  /** Whether the triplet eighth follows the KIT: each "and" where the drum
   * loop's skip note measured (swingFeel.ts `followsKit`, docs/swing-plan.md
   * §4.1). The default; choosing the exact triplet turns it off. */
  swingWithKit: boolean;
  /** Where the melody sits against the kit: its swung downbeats laid back
   * (On top / Centre / Behind), its offbeats on the ride (bandFeel.ts). */
  melodyPlacement: Placement;
  /** How the melody phrases a swing line: as written, or swung. */
  melodyPhrasing: MelodyPhrasing;
  /** Where the bass sits against the kit, every note alike. */
  bassPlacement: Placement;
  /** Which beats the walking bass leans on. */
  bassAccent: BassAccent;
  /** Eighth-note skips into the next beat (an embellishment, like ghosts). */
  bassSkips: boolean;
  /** Where the comp sits against the kit, every attack alike. */
  compPlacement: Placement;
  /** How much the band swings against its kit under the Kit choice, as a
   *  fraction of the beat added to the kit's ratio (swingFeel.ts). */
  swingOffset: number;
  /** How much each part swings against the band, likewise. The drums are a
   *  recording and keep the loop's own swing. */
  melodySwingOffset: number;
  compSwingOffset: number;
  bassSwingOffset: number;
  bassGhosts: boolean;
  bassAnticipate: boolean;
  bassTriplets: boolean;
  bassAmount: number;
  /** Which instrument plays the comp channel. A band setting rather than a
   * global one, because it belongs to the arrangement of THIS tune — the same
   * reasoning as comp style, and it is remembered per tune for free. */
  compInstrument: CompInstrument;
  /** Which voicing family the piano comp plays (inert on guitar). A band
   * setting for the same reason as the instrument: it is the arrangement of
   * THIS tune, and per-tune memory remembers it for free. */
  compPianoFamily: PianoCompFamily;
  compStyle: CompStyle;
  compAmount: number;
  drums: boolean;
  drumLoopKey: string;
}

export interface StylePreset {
  id: StyleId;
  label: string;
  category: string;
  band: StyleBand;
}

/** Every preset turns the bass line and drums on with no embellishments but
 * a swinging line's skips — a clean starting point the user then tweaks. A
 * swinging preset takes the swing feel (the melody laid back and phrased, the
 * bass on 2 and 4 and skipping at the rate for the tempo), a straight one the
 * straight feel (every part centred, the line as written). */
function band(
  over: Partial<StyleBand> &
    Pick<StyleBand, 'bassStyle' | 'bassFeel' | 'bassSwing' | 'compStyle' | 'drumLoopKey'>,
): StyleBand {
  return {
    ...(over.bassSwing ? SWING_BAND_FEEL : STRAIGHT_BAND_FEEL),
    bassline: true,
    bassSolo: false,
    bassGhosts: false,
    bassAnticipate: false,
    bassTriplets: false,
    bassAmount: 0.35,
    bassFollowForm: true,
    swingFeel: DEFAULT_SWING_FEEL,
    // The house band (user ruling Sep 2026): the comp is a pianist's LEFT
    // hand, the melody on piano above it — the trio. Every preset starts there;
    // the mixer changes it per tune.
    compInstrument: 'piano-left',
    compPianoFamily: 'lh-rootless',
    compAmount: 0.5,
    drums: true,
    ...over,
  };
}

export const STYLE_PRESETS: StylePreset[] = [
  {
    id: 'swing',
    label: 'Medium Swing',
    category: 'Jazz',
    band: band({
      bassStyle: 'walking',
      bassFeel: 'four',
      bassSwing: true,
      compStyle: 'interactive',
      drumLoopKey: '5b2bb8454ff3019d',
    }),
  },
  {
    id: 'up-swing',
    label: 'Up-Tempo Swing',
    category: 'Jazz',
    band: band({
      bassStyle: 'walking',
      bassFeel: 'four',
      bassSwing: true,
      compStyle: 'interactive',
      drumLoopKey: '4a678093e205e40b',
    }),
  },
  {
    id: 'ballad',
    label: 'Ballad',
    category: 'Jazz',
    band: band({
      bassStyle: 'walking',
      bassFeel: 'two',
      bassSwing: true,
      compStyle: 'interactive',
      drumLoopKey: 'b0ba467819c47bdb',
    }),
  },
  {
    id: 'bossa',
    label: 'Bossa Nova',
    category: 'Latin',
    band: band({
      bassStyle: 'latin',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'interactive',
      drumLoopKey: '17eebd9e17850a8f',
    }),
  },
  {
    id: 'samba',
    label: 'Samba',
    category: 'Latin',
    band: band({
      bassStyle: 'latin',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'interactive',
      drumLoopKey: '3b57340ab8554086',
    }),
  },
  {
    id: 'choro',
    label: 'Choro',
    category: 'Brazilian',
    band: band({
      bassStyle: 'latin',
      bassFeel: 'four',
      bassSwing: false,
      // Push, not held: choro comping anticipates the downbeat under the
      // running sixteenths — a held pad just sits on top of them.
      compStyle: 'push',
      // "Choro — pandeiro, slow" (DrumGenius 486): a real choro pandeiro whose
      // 4-beat bar tiles a 2/4 chart; at Lôro's ♩=105 it runs at 0.82×.
      drumLoopKey: 'ui7dsre342',
    }),
  },
  {
    id: 'shuffle',
    label: 'Blues Shuffle',
    category: 'Blues',
    band: band({
      bassStyle: 'shuffle',
      bassFeel: 'four',
      bassSwing: true,
      compStyle: 'freestyle',
      drumLoopKey: '5a06fada12fa960c',
    }),
  },
  {
    id: 'funk',
    label: 'Funk',
    category: 'Funk',
    band: band({
      bassStyle: 'roots-fifths',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'funk',
      drumLoopKey: '6k66ca07fb4z5666',
    }),
  },
  {
    id: 'waltz',
    label: 'Jazz Waltz',
    category: 'Jazz',
    band: band({
      bassStyle: 'walking',
      bassFeel: 'four',
      bassSwing: true,
      compStyle: 'interactive',
      drumLoopKey: '62f9fc208d57ffa6',
    }),
  },
  {
    id: 'even-8ths',
    label: 'Straight 8ths',
    category: 'Modern Jazz',
    band: band({
      bassStyle: 'walking',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'held',
      drumLoopKey: 'a76c2e4e2c492d05',
    }),
  },
  {
    id: 'boogaloo',
    label: 'Boogaloo',
    category: 'Soul Jazz',
    band: band({
      bassStyle: 'roots-fifths',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'push',
      drumLoopKey: 'bd2f2a232b347594',
    }),
  },
  {
    id: 'calypso',
    label: 'Calypso',
    category: 'Caribbean',
    band: band({
      bassStyle: 'latin',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'held',
      drumLoopKey: '02e9508d9d6e7539',
    }),
  },
  {
    id: 'cha-cha',
    label: 'Cha-cha',
    category: 'Afro-Cuban',
    band: band({
      bassStyle: 'latin',
      bassFeel: 'four',
      bassSwing: false,
      compStyle: 'held',
      drumLoopKey: '1h2508298fa6dex3',
    }),
  },
];

const BY_ID = new Map(STYLE_PRESETS.map((p) => [p.id, p]));

/** Interactive follows an explicit funk choice or the funk preset's drum
 * groove even when the comp style is changed. Straight bass alone does not
 * imply funk (it could be bossa, rock, or modern jazz).
 */
export function compFeelForBand(
  band: Pick<StyleBand, 'bassStyle' | 'compStyle' | 'drumLoopKey'>,
): CompFeel {
  if (band.compStyle === 'funk') return 'funk';
  if (band.bassStyle === 'latin') return 'latin';
  return band.drumLoopKey === BY_ID.get('funk')?.band.drumLoopKey ? 'funk' : 'swing';
}

export function getStylePreset(id: StyleId): StylePreset | undefined {
  return BY_ID.get(id);
}

// Keyword → preset. Order matters: more specific words are tested first (a
// "Latin-Samba" should hit samba, not the generic latin/bossa). Returns null
// when nothing matches, so an unrecognised style leaves the band untouched.
const KEYWORDS: [RegExp, StyleId][] = [
  [/choro|chorinho/i, 'choro'],
  [/samba|partido|baiao/i, 'samba'],
  [/bossa/i, 'bossa'],
  [/calypso/i, 'calypso'],
  [/boogaloo/i, 'boogaloo'],
  [/shuffle/i, 'shuffle'],
  [/even.?8|straight.?8/i, 'even-8ths'],
  [/funk|fusion|soul/i, 'funk'],
  [/waltz|jazz.?waltz|3\/4/i, 'waltz'],
  [/ballad|slow/i, 'ballad'],
  [/up.?tempo|fast|bright|burn/i, 'up-swing'],
  [/latin|bolero/i, 'bossa'],
  [/swing|medium|bop|jazz/i, 'swing'],
];

/**
 * Styles we have NO curated groove for (#175).
 *
 * Bossa is not a curated match for Afro-Cuban material: a mambo, a songo and
 * a Night-In-Tunisia A section are not bossas, and offering one as though it
 * were a considered choice is worse than offering nothing — it hides a
 * missing decision behind a plausible sound. These are checked BEFORE the
 * keyword table because iReal writes them as "Latin-Afro Cuban", which the
 * generic `latin` row would otherwise swallow.
 */
const UNCURATED = /afro|mambo|songo|guaguanc|rumba|salsa|timba|clave/i;

/** Map a free-text style label (as typed, or from an iReal import) to a preset id. */
export function matchStylePreset(style: string | undefined | null): StyleId | null {
  if (!style) return null;
  // Cha-cha is the one precise Afro-Cuban feel this catalogue now carries.
  // Match it before the broad guard so "Afro-Cuban Cha-cha" remains usable;
  // generic Afro-Cuban, mambo and songo labels still stay visibly undecided.
  if (/cha.?cha/i.test(style)) return 'cha-cha';
  // No match keeps the current band and leaves the choice visibly unmade,
  // which is the honest answer until the catalogue has the real thing.
  if (UNCURATED.test(style)) return null;
  for (const [re, id] of KEYWORDS) if (re.test(style)) return id;
  return null;
}

export const GROOVE_KEYS = [
  'bassStyle',
  'bassFeel',
  'bassSwing',
  'compStyle',
  'drumLoopKey',
] as const satisfies readonly (keyof StyleBand)[];

const DETAIL_KEYS = [
  'swingFeel',
  'swingWithKit',
  'melodyPlacement',
  'melodyPhrasing',
  'bassPlacement',
  'bassAccent',
  'bassSkips',
  'compPlacement',
  'swingOffset',
  'melodySwingOffset',
  'compSwingOffset',
  'bassSwingOffset',
  'bassFollowForm',
  'bassGhosts',
  'bassAnticipate',
  'bassTriplets',
  'bassAmount',
  'compAmount',
] as const satisfies readonly (keyof StyleBand)[];

/**
 * The band fields that make its FEEL: the groove plus its details. Everything
 * else in a band is the player's (the channel mutes, the comp instrument and
 * its voicings), which is why a playlist's call carries only these.
 */
export const FEEL_KEYS = [...GROOVE_KEYS, ...DETAIL_KEYS] as const;

/** What a preset's name carries once its details differ from the preset. */
export const STYLE_MODIFIED = ' · modified';

/** A groove keeps its name when channels are muted or instruments change.
 * Density and embellishment edits retain that identity but mark it modified;
 * a different combination of bass, comp and drum grooves is Custom. */
export function describeBandPreset(
  current: Partial<StyleBand>,
): { id: StyleId; label: string; modified: boolean } | null {
  const preset = STYLE_PRESETS.find((p) => GROOVE_KEYS.every((k) => current[k] === p.band[k]));
  if (!preset) return null;
  return {
    id: preset.id,
    label: preset.label,
    // Older stored bands and callers can omit details. Missing information
    // cannot establish a modification, but the core groove must match fully.
    modified: DETAIL_KEYS.some((k) => current[k] !== undefined && current[k] !== preset.band[k]),
  };
}

/** The recognisable groove of a live band, independent of mixer mutes. */
export function matchBandPreset(current: Partial<StyleBand>): StyleId | null {
  return describeBandPreset(current)?.id ?? null;
}
